import React, { useState } from 'react';
import api from '../services/api';
import { useFriends } from '../context/FriendContext';
import { GAME_LABELS, GAME_XP } from '../hooks/useGameChallenge';
import Icon from './Icon';

const ChallengeInviteModal = ({ game, onClose, onSent }) => {
  const { friends } = useFriends();
  const [sentTo, setSentTo]   = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError]     = useState('');

  const handleChallenge = async (friend) => {
    setLoading(friend._id); setError('');
    try {
      await api.post('/challenges', { userId: friend._id, game });
      setSentTo(friend._id);
      onSent?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Erreur lors de l\'envoi.');
    } finally { setLoading(null); }
  };

  const label = GAME_LABELS[game] ?? game;
  const xp    = GAME_XP[game] ?? 1000;

  return (
    <>
      <style>{`
        @keyframes ci-back{from{opacity:0}to{opacity:1}}
        @keyframes ci-card{0%{transform:scale(.5) translateY(40px);opacity:0}65%{transform:scale(1.04) translateY(-4px);opacity:1}82%{transform:scale(.98) translateY(2px)}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes ci-stripe{0%{transform:scaleX(0)}100%{transform:scaleX(1)}}
        @keyframes ci-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
      <div className="fixed inset-0 z-[300] flex items-center justify-center p-4"
        style={{ animation:'ci-back .25s ease forwards', background:'rgba(0,0,0,.92)', backdropFilter:'blur(12px)' }}
        onClick={onClose}>
        <div className="relative w-full max-w-sm rounded-2xl overflow-hidden"
          style={{
            border:'2px solid #e10600',
            background:'linear-gradient(160deg,#0c0c10 0%,#e1060010 100%)',
            boxShadow:'0 0 50px #e1060040,0 0 100px #e1060018',
            animation:'ci-card .6s cubic-bezier(.34,1.56,.64,1) .05s both',
          }}
          onClick={e => e.stopPropagation()}>

          <div style={{ height:4, background:'linear-gradient(90deg,#e10600 0%,transparent 80%)', animation:'ci-stripe .5s .15s ease-out both', transformOrigin:'left' }} />

          <div className="px-5 py-5 flex flex-col gap-4" style={{ animation:'ci-in .35s .3s ease both', opacity:0 }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-0.5">Défi</p>
                <h2 className="text-white font-black text-lg">{label}</h2>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.1)' }}>
                <span className="text-zinc-400 text-sm">✕</span>
              </button>
            </div>

            <div className="rounded-xl px-3 py-2 flex items-center gap-2"
              style={{ background:'rgba(225,6,0,.08)', border:'1px solid rgba(225,6,0,.2)' }}>
              <Icon name="trophy" size={16} />
              <p className="text-zinc-300 text-xs">Le gagnant remporte <strong className="text-f1red">{xp} XP</strong>. Le perdant ne gagne rien.</p>
            </div>

            <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Choisir un ami à défier</p>

            {friends.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">Tu n'as pas encore d'amis.<br/>Ajoutes-en depuis le Paddock.</p>
            ) : (
              <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
                {friends.map(f => {
                  const isSent    = sentTo === f._id;
                  const isLoading = loading === f._id;
                  return (
                    <button key={f._id}
                      onClick={() => !isSent && !isLoading && handleChallenge(f)}
                      disabled={isLoading || !!sentTo}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left disabled:opacity-50"
                      style={{
                        borderColor: isSent ? '#22c55e50' : 'rgba(255,255,255,.08)',
                        background:  isSent ? 'rgba(34,197,94,.06)' : 'rgba(255,255,255,.03)',
                      }}>
                      <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-zinc-700">
                        {f.avatar
                          ? <img src={f.avatar} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-xs font-black text-f1red">
                              {f.username?.[0]?.toUpperCase()}
                            </div>
                        }
                      </div>
                      <span className={`flex-1 text-sm font-semibold ${isSent ? 'text-green-400' : 'text-zinc-200'}`}>
                        {f.username}
                      </span>
                      {isSent
                        ? <span className="text-green-400 text-xs font-black">✓ Envoyé !</span>
                        : isLoading
                          ? <span className="text-zinc-500 text-xs">…</span>
                          : <span className="text-zinc-500 text-xs group-hover:text-white">Défier →</span>
                      }
                    </button>
                  );
                })}
              </div>
            )}

            {error && <p className="text-red-400 text-xs">{error}</p>}

            {sentTo && (
              <button onClick={onClose}
                className="w-full py-2.5 rounded-xl font-black text-sm text-white"
                style={{ background:'#e10600', boxShadow:'0 4px 16px #e1060040' }}>
                Fermer
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChallengeInviteModal;
